import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import './NotesHome.css';

export default function NotesHome({ userId }) {
  return (
    <main className="notes-home">
      <section className="notes-hero">
        <div className="notes-hero__content">
          <div className="notes-brand-kicker">MySimpleDB Notes</div>

          <h1>
            <span>Your ideas. Your reminders.</span>
            <span>Whenever and wherever you need them.</span>
          </h1>

          <p className="notes-hero__lead">
            Create and organize notes, keep them synchronized across your
            devices, and add reminders so important things do not get forgotten.
          </p>

          <div className="notes-hero__actions">
            <Link className="notes-btn notes-btn--primary" to="/create-account">
              Start Free
            </Link>

            <Link className="notes-btn notes-btn--secondary" to="/login">
              Sign In
            </Link>
          </div>

          <div className="notes-hero__small">
            One account for web and desktop access.
          </div>
        </div>

        <div
          className="notes-hero__visual"
          aria-label="MySimpleDB Notes preview"
        >
          <div className="sample-note sample-note--yellow sample-note--one">
            <div className="sample-note__title">Today</div>
            <div className="sample-note__line">Call about appointment</div>
            <div className="sample-note__line">Pick up groceries</div>
            <div className="sample-note__line">Finish project notes</div>
          </div>

          <div className="sample-note sample-note--blue sample-note--two">
            <div className="sample-note__title">Ideas</div>
            <div className="sample-note__line">Weekend project</div>
            <div className="sample-note__line">Vacation list</div>
          </div>

          <div className="sample-note sample-note--pink sample-note--three">
            <div className="sample-note__title">Reminder</div>
            <div className="sample-note__line">Friday • 10:00 AM</div>
            <div className="sample-note__line">SMS reminder ✓</div>
          </div>
        </div>
      </section>

      <section className="notes-section">
        <div className="notes-section__heading">
          <span>Stay organized</span>
          <h2>Everything you need for everyday notes</h2>
          <p>
            MySimpleDB Notes keeps the experience simple while giving you the
            tools to organize, find, and remember what matters.
          </p>
        </div>

        <div className="notes-feature-grid">
          <FeatureCard
            icon="✎"
            title="Create notes quickly"
            text="Capture ideas, tasks, lists, and information without digging through complicated menus."
          />
          <FeatureCard
            icon="↻"
            title="Sync across devices"
            text="Sign in on another computer and your notes are there, with changes synchronized automatically."
          />
          <FeatureCard
            icon="●"
            title="Organize with categories"
            text="Use categories and colors to make important notes easy to recognize and find."
          />
          <FeatureCard
            icon="◷"
            title="Set reminders"
            text="Attach a reminder date and time to a note so it can get your attention when you need it."
          />
          <FeatureCard
            icon="✉"
            title="SMS reminders"
            text="For important reminders, optionally receive a text message away from your computer."
          />
          <FeatureCard
            icon="⌕"
            title="Find notes fast"
            text="Search your notes instead of scrolling through everything you have saved."
          />
        </div>
      </section>

      <section className="notes-section notes-section--soft">
        <div className="notes-section__heading">
          <span>Simple workflow</span>
          <h2>From thought to reminder in three steps</h2>
        </div>

        <div className="notes-steps">
          <Step number="1" title="Create a note">
            Write down an idea, task, reminder, list, or anything you want to
            keep.
          </Step>
          <Step number="2" title="Organize it">
            Choose a category and color, and add a reminder when appropriate.
          </Step>
          <Step number="3" title="Use it anywhere">
            Open your notes again from the web or one of your signed-in devices.
          </Step>
        </div>
      </section>

      <section className="notes-section">
        <div className="notes-split">
          <div>
            <div className="notes-section__heading notes-section__heading--left">
              <span>Reminders</span>
              <h2>A note can do more than sit on your screen.</h2>
              <p>
                Set a date and time for something important. MySimpleDB Notes
                can remind you on your computer, and you can optionally add an
                SMS reminder when you want an extra nudge.
              </p>
            </div>

            <ul className="notes-check-list">
              <li>Reminder dates and times</li>
              <li>Desktop reminders</li>
              <li>Optional SMS reminders</li>
              <li>Reminder status stays with the note</li>
            </ul>
          </div>

          <div className="reminder-demo">
            <div className="reminder-demo__label">REMINDER</div>
            <div className="reminder-demo__title">Call Dr. Smith</div>
            <div className="reminder-demo__time">Tomorrow • 10:00 AM</div>
            <div className="reminder-demo__sms">SMS reminder ✓</div>
          </div>
        </div>
      </section>

      <section className="notes-section notes-section--soft">
        <div className="notes-section__heading">
          <span>Use Notes anywhere</span>
          <h2>One set of notes across your devices</h2>
        </div>

        <div className="notes-platform-grid">
          <PlatformCard
            title="Web"
            text="Access your notes from your browser."
          />
          <PlatformCard
            title="Windows"
            text="Keep MySimpleDB Notes close at hand on your PC."
          />
          <PlatformCard
            title="Mac"
            text="Use the desktop Notes experience on your Mac."
          />
          <PlatformCard
            title="Mobile"
            text="Keep your notes available while you are away from your computer."
          />
        </div>

        <div className="notes-center-action">
          <Link className="notes-btn notes-btn--secondary" to="/downloads">
            Downloads
          </Link>
        </div>
      </section>

      <section className="notes-section" id="training">
        <div className="notes-section__heading">
          <span>Getting started</span>
          <h2>Short training videos</h2>
          <p>
            This area is ready for your training videos. Replace the placeholder
            buttons with your video links as you publish them.
          </p>
        </div>

        <div className="notes-video-grid">
          <VideoCard title="Getting Started with MySimpleDB Notes" />
          <VideoCard title="Creating Your First Note" />
          <VideoCard title="Organizing Notes with Categories" />
          <VideoCard title="Setting Reminders" />
          <VideoCard title="Using SMS Reminders" />
          <VideoCard title="Using Notes on Multiple Computers" />
        </div>
      </section>

      <section className="notes-section notes-section--pricing">
        <div className="notes-pricing-card">
          <div className="notes-brand-kicker">MySimpleDB Notes</div>
          <h2>Start with a free trial.</h2>
          <p>
            Create an account and begin using Notes. Pricing details can be
            added here when you finalize the public plan.
          </p>

          <div className="notes-pricing-features">
            <span>✓ Notes synchronization</span>
            <span>✓ Categories and colors</span>
            <span>✓ Reminders</span>
            <span>✓ Desktop access</span>
            <span>✓ Web access</span>
          </div>

          <Link className="notes-btn notes-btn--primary" to="/create-account">
            Start Free
          </Link>
        </div>
      </section>

      <section className="notes-section">
        <div className="notes-section__heading">
          <span>Questions</span>
          <h2>Frequently asked questions</h2>
        </div>

        <div className="notes-faq">
          <Faq
            question="Can I use Notes on more than one computer?"
            answer="Yes. Sign in with the same account and your notes can synchronize across your devices."
          />
          <Faq
            question="Can I access my notes from a browser?"
            answer="Yes. MySimpleDB Notes includes a web experience in addition to the desktop applications."
          />
          <Faq
            question="Can Notes remind me by text message?"
            answer="Yes. When SMS reminders are enabled for a note, MySimpleDB Notes can send a text reminder."
          />
          <Faq
            question="Can I upgrade to the full MySimpleDB product later?"
            answer="Your Notes account is designed to remain compatible with the broader MySimpleDB product so upgrading later can be straightforward."
          />
        </div>
      </section>

      <section className="notes-final-cta">
        <div>
          <h2>Ready to put your notes to work?</h2>
          <p>Create your MySimpleDB Notes account and get started.</p>
        </div>

        <div className="notes-final-cta__actions">
          <Link className="notes-btn notes-btn--light" to="/create-account">
            Start Free
          </Link>
          <Link className="notes-btn notes-btn--outline-light" to="/login">
            Sign In
          </Link>
        </div>
      </section>
    </main>
  );
}

function FeatureCard({ icon, title, text }) {
  return (
    <article className="notes-feature-card">
      <div className="notes-feature-card__icon" aria-hidden="true">
        {icon}
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

function Step({ number, title, children }) {
  return (
    <article className="notes-step">
      <div className="notes-step__number">{number}</div>
      <div>
        <h3>{title}</h3>
        <p>{children}</p>
      </div>
    </article>
  );
}

function PlatformCard({ title, text }) {
  return (
    <article className="notes-platform-card">
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

function VideoCard({ title }) {
  return (
    <article className="notes-video-card">
      <div className="notes-video-card__preview" aria-hidden="true">
        <span>▶</span>
      </div>
      <div className="notes-video-card__body">
        <h3>{title}</h3>
        <button type="button" disabled title="Add video URL when available">
          Coming soon
        </button>
      </div>
    </article>
  );
}

function Faq({ question, answer }) {
  return (
    <details className="notes-faq__item">
      <summary>{question}</summary>
      <p>{answer}</p>
    </details>
  );
}
